import { useState, useEffect, useRef } from "react";
import axios from "axios";
import "@/App.css";
import Sidebar from "./components/Sidebar";
import HeroSection from "./components/HeroSection";
import FeatureShowcase from "./components/FeatureShowcase";
import AIDemoSection from "./components/AIDemoSection";
import PricingSection from "./components/PricingSection";
import AboutReviews from "./components/AboutReviews";
import FAQSection from "./components/FAQSection";
import BlogSection from "./components/BlogSection";
import CTASection from "./components/CTASection";

const sectionIds = ["hero", "features", "ai-demo", "pricing", "about", "blog", "faq"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://api.relio.in";
const API = `${BACKEND_URL}/api`;

function App() {
  const [activeSection, setActiveSection] = useState("hero");
  const mainRef = useRef(null);

  // Send status ping on load
  useEffect(() => {
    const pingStatus = async () => {
      try {
        await axios.post(`${API}/status`, { client_name: "relo-dashboard-visitor" });
      } catch (err) {
        // silently ignore tracking errors
      }
    };
    pingStatus();
  }, []);

  // Observe which section is currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          // Pick the one with highest intersection ratio
          const best = visible.reduce((a, b) =>
            a.intersectionRatio > b.intersectionRatio ? a : b
          );
          setActiveSection(best.target.id);
        }
      },
      {
        root: mainRef.current,
        threshold: [0.2, 0.5],
        rootMargin: "-10% 0px -10% 0px",
      }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavigate = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div
      data-testid="app-root"
      className="flex h-screen w-full overflow-hidden bg-white"
    >
      <Sidebar activeSection={activeSection} onNavigate={handleNavigate} />

      <main
        ref={mainRef}
        data-testid="main-content"
        className="flex-1 h-full overflow-y-auto bg-[#fcf8fb] rounded-l-[32px] md:rounded-l-[40px] shadow-2xl relative"
      >
        <div className="flex flex-col gap-0">
          <HeroSection />
          <FeatureShowcase />
          <AIDemoSection />
          <PricingSection />
          <AboutReviews />
          <BlogSection />
          <FAQSection />
          <CTASection />
        </div>
      </main>
    </div>
  );
}

export default App;
